/* eslint-disable @typescript-eslint/no-explicit-any */
// FIXME: fix any

import { PanelState } from '@/app/navigation/panel-state-utils';
import { PanelContext } from '@/components/Layout/Panel/PanelContext';
import { panelStateLogger } from '@/config/logging';
import { useContext, useEffect } from 'react';

/**
 * usePanelState Hook
 *
 * Provides a useState-like API for managing panel state in URL parameters.
 * State is automatically persisted to the URL with prefixed namespacing
 * to prevent collisions between different panels and stacks.
 */
export interface PanelStateAPI<T extends PanelState = PanelState> {
  state: T;

  /**
   * Update panel state (supports both object and function updates)
   */
  setState: (newState: T | ((prev: T) => T)) => void;

  /** Clear all state for this panel */
  clearState: () => void;
}

export interface UsePanelStateOptions {
  /**
   * Optional prefix for state keys to avoid conflicts when multiple
   * components use panel state in the same panel (e.g., multiple tables in tabs).
   * Example: prefix='t.' will namespace keys as 't.filter', 't.selection', etc.
   */
  prefix?: string;
}

export function usePanelState<T extends PanelState = PanelState>(
  options?: UsePanelStateOptions,
) {
  const prefix = options?.prefix || '';
  const panelCtx = useContext<PanelStateAPI<T>>(PanelContext as any);
  if (!panelCtx) {
    throw new Error('usePanelState must be used within a Panel component');
  }

  if (!import.meta.env.DEV) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
      panelStateLogger.info('Panel state changed', { state: panelCtx.state });
    }, [panelCtx.state, panelCtx.setState, panelCtx.clearState]);
  }

  // If no prefix skip the prefixing logic
  if (!prefix) {
    return [panelCtx.state, panelCtx.setState, panelCtx.clearState] as const;
  }

  // Create prefixed view of the state
  const prefixedState = Object.entries(panelCtx.state).reduce(
    (acc, [key, value]) => {
      if (key.startsWith(prefix)) {
        (acc as Record<string, unknown>)[key.slice(prefix.length)] = value;
      }
      return acc;
    },
    {} as T,
  );

  // Create prefixed setState
  const prefixedSetState = (newState: T | ((prev: T) => T)) => {
    panelCtx.setState((prev) => {
      const currentPrefixed = Object.entries(prev).reduce(
        (acc, [key, value]) => {
          if (key.startsWith(prefix)) {
            (acc as Record<string, unknown>)[key.slice(prefix.length)] = value;
          }
          return acc;
        },
        {} as T,
      );

      const nextPrefixed =
        typeof newState === 'function' ? newState(currentPrefixed) : newState;

      // Remove old prefixed keys and add new ones
      const withoutPrefix = Object.entries(prev).reduce((acc, [key, value]) => {
        if (!key.startsWith(prefix)) {
          acc[key] = value;
        }
        return acc;
      }, {} as PanelState);

      const withNewPrefix = Object.entries(nextPrefixed).reduce(
        (acc, [key, value]) => {
          acc[prefix + key] = value;
          return acc;
        },
        {} as PanelState,
      );

      return { ...withoutPrefix, ...withNewPrefix } as T;
    });
  };

  // Create prefixed clearState
  const prefixedClearState = () => {
    panelCtx.setState((prev) => {
      return Object.entries(prev).reduce((acc, [key, value]) => {
        if (!key.startsWith(prefix)) {
          (acc as Record<string, unknown>)[key] = value;
        }
        return acc;
      }, {} as T);
    });
  };

  return [prefixedState, prefixedSetState, prefixedClearState] as const;
}
